/** Convert a key like "avatar_url" or "firstName" to "Avatar Url" or "First Name" */
export function humanizeKey(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Person fields that look identical across groups (several "Email"s, several "Name"s). */
const PERSON_KEYS = new Set([
  'name', 'email', 'first_name', 'last_name', 'firstName', 'lastName', 'user_name', 'user_email',
]);

/** Friendly names for the top-level groups of an event app's (Calendly, Acuity) payload. */
const EVENT_GROUP_LABELS: Record<string, string> = {
  contact: 'Invitee',
  assignee: 'Host',
  event: 'Meeting',
  payload: 'Raw booking data',
};

/**
 * Who a person field of an event app belongs to: the invitee (person who booked)
 * or the host (team member who owns the meeting). Null for non-person fields.
 */
function getEventFieldOwner(path: string): 'Invitee' | 'Host' | null {
  const leaf = path.split('.').pop() || path;
  if (!PERSON_KEYS.has(leaf)) return null;
  if (path.includes('event_memberships')) return 'Host';
  const top = path.replace(/^\$\./, '').split('.')[0];
  if (top === 'assignee') return 'Host';
  // contact.scheduled_event.name is the meeting's name, not a person's
  if (path.includes('scheduled_event')) return null;
  if (top === 'contact' || top === 'payload') return 'Invitee';
  return null;
}

/**
 * Display label for a mapped event field. For event apps, person fields get an
 * owner prefix ("Invitee Email", "Host Email") so look-alike fields are told apart.
 */
export function getFieldLabel(path: string, isEventApp: boolean): string {
  const leaf = path.split('.').pop() || path;
  const owner = isEventApp ? getEventFieldOwner(path) : null;
  if (!owner) return humanizeKey(leaf);
  return `${owner} ${humanizeKey(leaf.replace(/^user_/, ''))}`;
}

/** Display label for a field group header ("contact" → "Invitee" for event apps). */
export function getFieldGroupLabel(group: string, isEventApp: boolean): string {
  return (isEventApp && EVENT_GROUP_LABELS[group]) || group;
}
